import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Camera, Layers } from "lucide-react";
import type { EchoflicksSessionEvent, EchoflicksSessionMedia } from "../types";
import { getDefaultAwsMediaBucket } from "../providers/aws/client";
import { isImage } from "./sessionUtils";
import { parallaxMoveLabel } from "./sceneBreakdown";
import {
  parallaxPreviewFromEvents,
  type ParallaxPreviewModel,
  type ParallaxPreviewPick,
} from "./parallaxPreview";
import { useMediaUrl } from "./useMediaUrl";

/**
 * "Parallax Preview" — the observe pilot's surface on the echo details page.
 *
 * Shows the parallax shots the director WOULD have pinned (shadow mode; the
 * delivered echo is untouched) next to the standalone preview clips the composer
 * rendered post-delivery on the production i2v providers, plus the per-photo
 * scoring verdicts. Renders nothing for echoes without an observed event, so the
 * section is invisible outside the pilot.
 */
export const ParallaxPreviewSection = ({
  events,
  media,
}: {
  events: EchoflicksSessionEvent[];
  media: EchoflicksSessionMedia[];
}) => {
  const model = useMemo(() => parallaxPreviewFromEvents(events), [events]);
  const mediaByFilename = useMemo(() => buildMediaMap(media), [media]);

  if (!model) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Layers className="size-5" />
          <CardTitle>Parallax Preview</CardTitle>
          <Badge variant="outline">Observe — not in the delivered echo</Badge>
        </div>
        <CardDescription>
          The depth scout&apos;s shadow picks for this echo and how each
          parallax move looks rendered on the production providers. The
          delivered video is untouched.
        </CardDescription>
        <div className="mt-1 flex flex-wrap gap-2">
          <Badge variant="secondary">{model.picks.length} planned</Badge>
          <Badge variant="outline">{model.renderedCount} preview clip(s)</Badge>
          {model.source ? (
            <Badge variant="outline">source: {model.source}</Badge>
          ) : null}
          {model.renderOutcome && model.renderOutcome !== "completed" ? (
            <Badge variant="destructive">render {model.renderOutcome}</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {model.picks.map((pick) => (
            <PickCard
              key={pick.file}
              pick={pick}
              model={model}
              media={mediaByFilename.get(pick.file)}
            />
          ))}
        </div>
        {model.considered.length > 0 ? (
          <details className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Why these photos — per-photo scoring ({model.considered.length})
            </summary>
            <div className="mt-2 space-y-1">
              {model.considered.map((row) => (
                <div
                  key={row.file}
                  className="flex flex-wrap items-center gap-2 text-xs"
                >
                  <span className="min-w-0 flex-1 truncate font-mono">
                    {row.file}
                  </span>
                  {row.quality != null ? (
                    <span className="text-muted-foreground">
                      q={row.quality}
                    </span>
                  ) : null}
                  {row.kind ? (
                    <span className="text-muted-foreground">{row.kind}</span>
                  ) : null}
                  <Badge
                    variant={row.verdict === "picked" ? "default" : "outline"}
                  >
                    {row.verdict.replaceAll("_", " ") || "unknown"}
                  </Badge>
                </div>
              ))}
            </div>
          </details>
        ) : null}
      </CardContent>
    </Card>
  );
};

const PickCard = ({
  pick,
  model,
  media,
}: {
  pick: ParallaxPreviewPick;
  model: ParallaxPreviewModel;
  media?: EchoflicksSessionMedia;
}) => {
  const photo = useMediaUrl(media ?? null);
  const clip = useMediaUrl(
    pick.clipKey
      ? {
          storage_bucket: model.storageBucket ?? getDefaultAwsMediaBucket(),
          storage_path: pick.clipKey,
          storage_is_public: false,
        }
      : null,
  );
  const moveLabel = parallaxMoveLabel(pick.move) ?? pick.move;

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="gap-1">
          <Layers className="size-3" /> Parallax
          {moveLabel ? ` · ${moveLabel}` : ""}
        </Badge>
        {pick.kind ? <Badge variant="secondary">{pick.kind}</Badge> : null}
        {pick.quality != null ? (
          <Badge variant="outline">q={pick.quality}</Badge>
        ) : null}
      </div>
      {pick.anchor || pick.background ? (
        <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <Camera className="size-3" />
          {pick.anchor ? (
            <span className="font-medium">{pick.anchor}</span>
          ) : null}
          {pick.background ? (
            <>
              {" → "}
              <span className="font-medium">{pick.background}</span>
            </>
          ) : null}
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <div className="overflow-hidden rounded-md bg-black">
          {photo.url && isImage(media?.mime_type) ? (
            <img
              src={photo.url}
              alt={pick.file}
              className="aspect-video w-full object-contain"
              loading="lazy"
              onError={photo.refresh}
            />
          ) : (
            <div className="flex aspect-video items-center justify-center text-xs text-white/60">
              {pick.file}
            </div>
          )}
        </div>
        <div className="overflow-hidden rounded-md bg-black">
          {pick.clipStatus === "rendered" && clip.url ? (
            <video
              src={clip.url}
              controls
              onError={clip.refresh}
              className="aspect-video w-full bg-black object-contain"
            />
          ) : (
            <div className="flex aspect-video items-center justify-center px-2 text-center text-xs text-white/60">
              {pick.clipStatus === "pending"
                ? "Preview clip not rendered yet…"
                : pick.clipStatus === "rendered"
                  ? "Preparing a private signed URL…"
                  : (pick.clipError ?? `Preview ${pick.clipStatus}`)}
            </div>
          )}
        </div>
      </div>
      <div className="truncate text-xs text-muted-foreground">
        Source: {pick.file}
      </div>
    </div>
  );
};

function buildMediaMap(media: EchoflicksSessionMedia[]) {
  const byFilename = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const filename =
      item.s3_key?.split("/").at(-1) ?? item.local_path?.split(/[\\/]/).at(-1);
    if (filename && !byFilename.has(filename)) {
      byFilename.set(filename, item);
    }
  }
  return byFilename;
}
